import { compareIgnoreCase, containsIgnoreCase } from "@ane/core/lib/utils/CommonStringUtils";
import { MSFT_WORKSPACE_ID_PROD, MSFT_WORKSPACE_NAME_PROD, MSFT_WORKSPACE_NAME_LOCAL, MSFT_EG_ROOT, MSFT_WORKSPACE_ID_LOCAL } from "@ane/core/lib/services/EnvironmentService";
const getExperimentationGroupLabel = (options = { lowerCase: false, indefiniteArticle: false }) => {
  const { lowerCase, indefiniteArticle } = options;
  let value = "Experimentation group";
  if (indefiniteArticle) {
    value = `An ${value.toLowerCase()}`;
  }
  value = !!lowerCase ? value.toLowerCase() : value;
  return value;
};
const getTenantFromManagementGroup = (managementGroup) => !!managementGroup ? `${managementGroup.split("/").slice(1).shift()}` : "";
const getBudgetAreaFromManagementGroup = (managementGroup) => {
  if (!!managementGroup) {
    const splitMg = managementGroup.split("/");
    if (splitMg.length >= 3) {
      return splitMg[2];
    }
  }
  return void 0;
};
const splitManagementGroupPath = (path) => {
  return (path || "").split("/").filter((s) => !!s);
};
const isMsftWorkspaceById = (workspaceId) => {
  return !workspaceId || compareIgnoreCase(workspaceId, MSFT_WORKSPACE_ID_PROD);
};
const isClassicWorkspaceById = (workspaceId) => {
  return isMsftWorkspaceById(workspaceId) || isTestMsftWorkspaceById(workspaceId);
};
const isClassicWorkspaceByName = (workspaceName) => {
  return !workspaceName || containsIgnoreCase([MSFT_WORKSPACE_NAME_PROD, MSFT_WORKSPACE_NAME_LOCAL, MSFT_EG_ROOT], workspaceName);
};
const isTestMsftWorkspaceById = (workspaceId) => {
  return !workspaceId || compareIgnoreCase(workspaceId, MSFT_WORKSPACE_ID_LOCAL);
};
const extractWorkspaceName = (workspaceId, path) => {
  const segments = splitManagementGroupPath(path);
  if (isMsftWorkspaceById(workspaceId)) {
    return MSFT_WORKSPACE_NAME_PROD;
  } else if (isTestMsftWorkspaceById(workspaceId)) {
    return MSFT_WORKSPACE_NAME_LOCAL;
  } else {
    return (segments == null ? void 0 : segments[0]) || "";
  }
};
const extractWorkspaceGroup = (workspaceId, path) => {
  const segments = splitManagementGroupPath(path);
  if (isClassicWorkspaceById(workspaceId)) {
    return segments[0] || "";
  } else {
    return segments.length > 1 ? segments[1] : "";
  }
};
const isRootManagementGroup = (managementGroup, isExperimentationGroupId = false) => {
  return !!managementGroup && !getManagementGroupLevels(managementGroup, isExperimentationGroupId).level2;
};
const isRootExperimentationGroup = (experimentationGroup, isExperimentationGroupId = false) => {
  const delimiter = isExperimentationGroupId ? "~" : "/";
  const regex = new RegExp(`[^${delimiter}]+`, "g");
  const value = (experimentationGroup == null ? void 0 : experimentationGroup.match(regex)) || [];
  return value.length == 1;
};
const getManagementGroupLevels = (managementGroup, isExperimentationGroupId = false) => {
  if (!managementGroup) {
    return { level1: "", level2: "", level3: "" };
  }
  let { l1 = "", l2 = "", l3 = "" } = {};
  const slashPositions = findCharacterPositions(managementGroup, isExperimentationGroupId ? "~" : "/");
  l1 = managementGroup;
  if (slashPositions.length >= 2) {
    l1 = managementGroup.slice(0, slashPositions[1]);
    l2 = managementGroup;
  }
  if (slashPositions.length >= 3) {
    l2 = managementGroup.slice(0, slashPositions[2]);
    l3 = managementGroup;
  }
  if (slashPositions.length >= 4) {
    l3 = managementGroup.slice(0, slashPositions[3]);
  }
  return { level1: l1, level2: l2, level3: l3 };
};
const findCharacterPositions = (str, c) => {
  const indices = [];
  for (var i = 0; i < str.length; i++) {
    if (str[i] === c)
      indices.push(i);
  }
  return indices;
};
const getExperimentationPathFromId = (workspaceId, id) => {
  const searchRegExp = /~/g;
  const replaceWith = "/";
  let path = id == null ? void 0 : id.replace(searchRegExp, replaceWith);
  path = getDataPlaneFormattedPath(path);
  if (isClassicWorkspaceById(workspaceId) && path.startsWith("/microsoft")) {
    path = path.substring(`/microsoft`.length);
  }
  if (path == null || path.length == 0) {
    return "/";
  }
  return path;
};
const getExperimentationGroupIdFromPath = (workspaceId, path) => {
  path = getDataPlaneFormattedPath(path);
  return transformPathToId(workspaceId, path);
};
const getDataPlaneFormattedPath = (path) => {
  if (path == null || path.length == 0) {
    return "/";
  }
  if (!path.startsWith("/")) {
    return "/" + path;
  }
  return path;
};
const transformPathToId = (workspaceId, path) => {
  workspaceId = (workspaceId == null ? void 0 : workspaceId.toLowerCase()) || "";
  if (isClassicWorkspaceById(workspaceId)) {
    path = `/microsoft${path}`;
  }
  var id = path.toLowerCase();
  const searchRegExp = /\//g;
  const replaceWith = "~";
  id = id.replace(searchRegExp, replaceWith);
  if (id.startsWith("~")) {
    id = id.substring(1);
  }
  if (id.endsWith("~") && id.length > 1) {
    id = id.substring(0, id.length - 1);
  }
  var pattern = new RegExp("[^a-z0-9~]", "g");
  id = id.replace(pattern, "");
  checkExperimentationGroupId(id);
  return id;
};
const checkExperimentationGroupId = (input) => {
  var pattern = new RegExp("^[a-z][a-z0-9~]+$", "g");
  let isCorrectPattern = pattern.test(input);
  if (!isCorrectPattern) {
    const error = new Error(`Experimentation group ${input} doesn't match regex pattern ${pattern}`);
    error.isUserError = true;
    throw error;
  }
  if (input.includes("~~") || input.endsWith("~")) {
    const error = new Error(`Experimentation group ${input} has consecutive ~ or it ends with ~`);
    error.isUserError = true;
    throw error;
  }
};
const isManagementGroupNameValid = (input) => {
  if (input) {
    if (input.length < 3 || input.length > 24) {
      return false;
    }
    var pattern = new RegExp("^[a-z0-9]+$");
    return pattern.test(input);
  }
  return false;
};
const getRootExperimentationGroup = (experimentationGroup) => {
  if (!experimentationGroup) {
    return "";
  }
  const segments = experimentationGroup.split("~");
  return segments.length > 0 ? segments[0] : "";
};
export {
  extractWorkspaceGroup,
  extractWorkspaceName,
  getBudgetAreaFromManagementGroup,
  getDataPlaneFormattedPath,
  getExperimentationGroupIdFromPath,
  getExperimentationGroupLabel,
  getExperimentationPathFromId,
  getManagementGroupLevels,
  getRootExperimentationGroup,
  getTenantFromManagementGroup,
  isClassicWorkspaceById,
  isClassicWorkspaceByName,
  isManagementGroupNameValid,
  isMsftWorkspaceById,
  isRootExperimentationGroup,
  isRootManagementGroup,
  isTestMsftWorkspaceById,
  splitManagementGroupPath
};
//# sourceMappingURL=ManagementGroup.utils.js.map
